import type { Theme } from '@/stores/ui.store';

import type { ITheme } from '@xterm/xterm';

/**
 * xterm theme derived from the app theme. Background/foreground come from the app's
 * CSS tokens (so the dock sits on the same surface as the chrome around it, glass
 * theme included); the 16 ANSI colours are Catppuccin Latte (light) / Frappe (dark),
 * chosen for contrast on those surfaces. xterm cannot read CSS variables and only
 * parses hex/rgb(a), so tokens are resolved through a computed-style probe.
 *
 * The probe is wrapped in an element carrying the requested mode class so the
 * result does not depend on whether ThemeProvider has already flipped <body> -
 * it toggles the class in an effect, i.e. after children render.
 */

const ANSI_LIGHT = {
  black: '#5c5f77',
  red: '#d20f39',
  green: '#40a02b',
  yellow: '#df8e1d',
  blue: '#1e66f5',
  magenta: '#ea76cb',
  cyan: '#179299',
  white: '#acb0be',
  brightBlack: '#6c6f85',
  brightRed: '#de293e',
  brightGreen: '#49af3d',
  brightYellow: '#eea02d',
  brightBlue: '#456eff',
  brightMagenta: '#fe85d8',
  brightCyan: '#2d9fa8',
  brightWhite: '#bcc0cc',
} satisfies Partial<ITheme>;

const ANSI_DARK = {
  black: '#51576d',
  red: '#e78284',
  green: '#a6d189',
  yellow: '#e5c890',
  blue: '#8caaee',
  magenta: '#f4b8e4',
  cyan: '#81c8be',
  white: '#b5bfe2',
  brightBlack: '#626880',
  brightRed: '#e78284',
  brightGreen: '#a6d189',
  brightYellow: '#e5c890',
  brightBlue: '#8caaee',
  brightMagenta: '#f4b8e4',
  brightCyan: '#81c8be',
  brightWhite: '#a5adce',
} satisfies Partial<ITheme>;

const FALLBACKS: Record<Theme, { background: string; foreground: string }> = {
  light: { background: '#f4f2ea', foreground: '#1c1c1a' },
  dark: { background: '#101112', foreground: '#e6e2d6' },
};

/** Resolve `var(--name)` under `scope` to an rgb()/rgba() string xterm can parse. */
function resolveToken(
  scope: HTMLElement,
  variable: string,
  fallback: string
): string {
  const probe = document.createElement('span');
  probe.style.color = `var(${variable}, ${fallback})`;
  scope.appendChild(probe);
  const resolved = getComputedStyle(probe).color;
  probe.remove();
  return resolved || fallback;
}

export function readTerminalTheme(mode: Theme): ITheme {
  const fallback = FALLBACKS[mode];
  if (typeof document === 'undefined') {
    return {
      ...fallback,
      ...(mode === 'dark' ? ANSI_DARK : ANSI_LIGHT),
    };
  }

  // Scoped so tokens resolve for `mode` regardless of the current <body> class.
  // Must be attached (computed styles need a rendered ancestor chain) but not
  // painted, hence visibility rather than display:none.
  const scope = document.createElement('div');
  scope.className = mode;
  scope.style.position = 'absolute';
  scope.style.visibility = 'hidden';
  scope.style.pointerEvents = 'none';
  document.body.appendChild(scope);

  // background-muted is one step below the surface the dock header sits on, so the
  // grid reads as slightly recessed rather than flush with the chrome
  const background = resolveToken(
    scope,
    '--background-muted',
    fallback.background
  );
  const foreground = resolveToken(
    scope,
    '--foreground-default',
    fallback.foreground
  );
  const selection = resolveToken(scope, '--selection-background', '#a3d2e6');
  scope.remove();

  const ansi = mode === 'dark' ? ANSI_DARK : ANSI_LIGHT;
  return {
    background,
    foreground,
    cursor: foreground,
    cursorAccent: background,
    selectionBackground: selection,
    selectionInactiveBackground: selection,
    ...ansi,
  };
}
